/**
 * Reusable Form Field Components
 *
 * All components are forwardRef-compatible with React Hook Form.
 * Each wraps a standard HTML element with:
 *  - Label
 *  - Helper text
 *  - Validation error message (rendered below the field)
 *  - Required asterisk
 */

import React, { forwardRef, useState } from 'react';
import { Eye, EyeOff, Upload, X } from 'lucide-react';

// ─────────────────────────────────────────────────────────────
// Shared FieldWrapper
// ─────────────────────────────────────────────────────────────
interface FieldWrapperProps {
  label?: string;
  required?: boolean;
  error?: string;
  hint?: string;
  id: string;
  children: React.ReactNode;
  className?: string;
}

const FieldWrapper: React.FC<FieldWrapperProps> = ({
  label, required, error, hint, id, children, className = '',
}) => (
  <div className={`form-group ${error ? 'form-group--error' : ''} ${className}`}>
    {label && (
      <label htmlFor={id} className="form-label">
        {label}
        {required && <span className="form-required" aria-hidden="true"> *</span>}
      </label>
    )}
    {children}
    {error && (
      <span id={`${id}-error`} className="form-error" role="alert">
        {error}
      </span>
    )}
    {hint && !error && (
      <span id={`${id}-hint`} className="form-hint">
        {hint}
      </span>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────
// TextField
// ─────────────────────────────────────────────────────────────
export interface TextFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  id: string;
  required?: boolean;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ label, error, hint, id, required, className = '', ...props }, ref) => (
    <FieldWrapper label={label} required={required} error={error} hint={hint} id={id}>
      <input
        ref={ref}
        id={id}
        type="text"
        className={`form-input ${error ? 'form-input--error' : ''} ${className}`}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        {...props}
      />
    </FieldWrapper>
  )
);
TextField.displayName = 'TextField';

// ─────────────────────────────────────────────────────────────
// EmailField
// ─────────────────────────────────────────────────────────────
export const EmailField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ label, error, hint, id, required, className = '', ...props }, ref) => (
    <FieldWrapper label={label} required={required} error={error} hint={hint} id={id}>
      <input
        ref={ref}
        id={id}
        type="email"
        className={`form-input ${error ? 'form-input--error' : ''} ${className}`}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        autoComplete="email"
        {...props}
      />
    </FieldWrapper>
  )
);
EmailField.displayName = 'EmailField';

// ─────────────────────────────────────────────────────────────
// PhoneField
// ─────────────────────────────────────────────────────────────
export const PhoneField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ label, error, hint, id, required, className = '', placeholder = '+1 000 000 0000', ...props }, ref) => (
    <FieldWrapper label={label} required={required} error={error} hint={hint} id={id}>
      <input
        ref={ref}
        id={id}
        type="tel"
        placeholder={placeholder}
        className={`form-input ${error ? 'form-input--error' : ''} ${className}`}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        autoComplete="tel"
        {...props}
      />
    </FieldWrapper>
  )
);
PhoneField.displayName = 'PhoneField';

// ─────────────────────────────────────────────────────────────
// UrlField
// ─────────────────────────────────────────────────────────────
export const UrlField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ label, error, hint, id, required, className = '', placeholder = 'https://', ...props }, ref) => (
    <FieldWrapper label={label} required={required} error={error} hint={hint} id={id}>
      <input
        ref={ref}
        id={id}
        type="url"
        placeholder={placeholder}
        className={`form-input ${error ? 'form-input--error' : ''} ${className}`}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        {...props}
      />
    </FieldWrapper>
  )
);
UrlField.displayName = 'UrlField';

// ─────────────────────────────────────────────────────────────
// PasswordField
// ─────────────────────────────────────────────────────────────
export const PasswordField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ label, error, hint, id, required, className = '', ...props }, ref) => {
    const [show, setShow] = useState(false);
    return (
      <FieldWrapper label={label} required={required} error={error} hint={hint} id={id}>
        <div className="form-input-group">
          <input
            ref={ref}
            id={id}
            type={show ? 'text' : 'password'}
            className={`form-input form-input--addon-right ${error ? 'form-input--error' : ''} ${className}`}
            aria-invalid={!!error}
            aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
            autoComplete="current-password"
            {...props}
          />
          <button
            type="button"
            className="form-input-addon"
            onClick={() => setShow(s => !s)}
            aria-label={show ? 'Hide password' : 'Show password'}
          >
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </FieldWrapper>
    );
  }
);
PasswordField.displayName = 'PasswordField';

// ─────────────────────────────────────────────────────────────
// NumberField
// ─────────────────────────────────────────────────────────────
export interface NumberFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  id: string;
  required?: boolean;
}

export const NumberField = forwardRef<HTMLInputElement, NumberFieldProps>(
  ({ label, error, hint, id, required, className = '', ...props }, ref) => (
    <FieldWrapper label={label} required={required} error={error} hint={hint} id={id}>
      <input
        ref={ref}
        id={id}
        type="number"
        className={`form-input ${error ? 'form-input--error' : ''} ${className}`}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        {...props}
      />
    </FieldWrapper>
  )
);
NumberField.displayName = 'NumberField';

// ─────────────────────────────────────────────────────────────
// DatePickerField
// ─────────────────────────────────────────────────────────────
export const DatePickerField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ label, error, hint, id, required, className = '', ...props }, ref) => (
    <FieldWrapper label={label} required={required} error={error} hint={hint} id={id}>
      <input
        ref={ref}
        id={id}
        type="date"
        className={`form-input ${error ? 'form-input--error' : ''} ${className}`}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        {...props}
      />
    </FieldWrapper>
  )
);
DatePickerField.displayName = 'DatePickerField';

// ─────────────────────────────────────────────────────────────
// TextAreaField
// ─────────────────────────────────────────────────────────────
export interface TextAreaFieldProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
  id: string;
  required?: boolean;
  rows?: number;
}

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  ({ label, error, hint, id, required, rows = 4, className = '', ...props }, ref) => (
    <FieldWrapper label={label} required={required} error={error} hint={hint} id={id}>
      <textarea
        ref={ref}
        id={id}
        rows={rows}
        className={`form-textarea ${error ? 'form-input--error' : ''} ${className}`}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        {...props}
      />
    </FieldWrapper>
  )
);
TextAreaField.displayName = 'TextAreaField';

// ─────────────────────────────────────────────────────────────
// SelectField
// ─────────────────────────────────────────────────────────────
export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectFieldProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  hint?: string;
  id: string;
  required?: boolean;
  options: SelectOption[];
  placeholder?: string;
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  ({ label, error, hint, id, required, options, placeholder, className = '', ...props }, ref) => (
    <FieldWrapper label={label} required={required} error={error} hint={hint} id={id}>
      <select
        ref={ref}
        id={id}
        className={`form-input form-select ${error ? 'form-input--error' : ''} ${className}`}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        {...props}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(opt => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>
    </FieldWrapper>
  )
);
SelectField.displayName = 'SelectField';

// ─────────────────────────────────────────────────────────────
// SwitchField
// ─────────────────────────────────────────────────────────────
export interface SwitchFieldProps {
  id: string;
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  error?: string;
}

export const SwitchField: React.FC<SwitchFieldProps> = ({
  id, label, description, checked, onChange, disabled = false, error,
}) => (
  <div className={`switch-field ${error ? 'form-group--error' : ''}`}>
    <div className="switch-field__content">
      <label htmlFor={id} className="switch-field__label">
        {label}
      </label>
      {description && (
        <p className="switch-field__description">{description}</p>
      )}
      {error && <span className="form-error">{error}</span>}
    </div>
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`switch-toggle ${checked ? 'switch-toggle--on' : ''} ${disabled ? 'switch-toggle--disabled' : ''}`}
    >
      <span className="switch-toggle__thumb" />
    </button>
  </div>
);

// ─────────────────────────────────────────────────────────────
// CheckboxField
// ─────────────────────────────────────────────────────────────
export interface CheckboxFieldProps {
  id: string;
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  error?: string;
}

export const CheckboxField: React.FC<CheckboxFieldProps> = ({
  id, label, description, checked, onChange, disabled = false, error,
}) => (
  <div className={`checkbox-field ${error ? 'form-group--error' : ''}`}>
    <input
      type="checkbox"
      id={id}
      checked={checked}
      onChange={e => onChange(e.target.checked)}
      disabled={disabled}
      className="checkbox-field__input"
    />
    <div className="checkbox-field__content">
      <label htmlFor={id} className="checkbox-field__label">{label}</label>
      {description && (
        <p className="checkbox-field__description">{description}</p>
      )}
      {error && <span className="form-error">{error}</span>}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// RadioGroupField
// ─────────────────────────────────────────────────────────────
export interface RadioOption {
  value: string;
  label: string;
  description?: string;
}

export interface RadioGroupFieldProps {
  id: string;
  label?: string;
  options: RadioOption[];
  value: string;
  onChange: (value: string) => void;
  error?: string;
  hint?: string;
  required?: boolean;
  inline?: boolean;
}

export const RadioGroupField: React.FC<RadioGroupFieldProps> = ({
  id, label, options, value, onChange, error, hint, required, inline = false,
}) => (
  <FieldWrapper label={label} required={required} error={error} hint={hint} id={id}>
    <div className={`radio-group ${inline ? 'radio-group--inline' : ''}`} role="radiogroup" aria-labelledby={label ? `${id}-label` : undefined}>
      {options.map(opt => (
        <label key={opt.value} className="radio-option">
          <input
            type="radio"
            name={id}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            className="radio-option__input"
          />
          <div className="radio-option__content">
            <span className="radio-option__label">{opt.label}</span>
            {opt.description && (
              <span className="radio-option__description">{opt.description}</span>
            )}
          </div>
        </label>
      ))}
    </div>
  </FieldWrapper>
);

// ─────────────────────────────────────────────────────────────
// MultiSelectField (checkbox list)
// ─────────────────────────────────────────────────────────────
export interface MultiSelectFieldProps {
  id: string;
  label?: string;
  options: SelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  error?: string;
  hint?: string;
  required?: boolean;
}

export const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  id, label, options, value, onChange, error, hint, required,
}) => {
  const toggle = (optValue: string) => {
    onChange(
      value.includes(optValue)
        ? value.filter(v => v !== optValue)
        : [...value, optValue]
    );
  };

  return (
    <FieldWrapper label={label} required={required} error={error} hint={hint} id={id}>
      <div className="multi-select" role="group" aria-labelledby={label ? `${id}-label` : undefined}>
        {options.map(opt => (
          <label key={opt.value} className="multi-select__option">
            <input
              type="checkbox"
              checked={value.includes(opt.value)}
              onChange={() => toggle(opt.value)}
              className="multi-select__checkbox"
            />
            <span className="multi-select__label">{opt.label}</span>
          </label>
        ))}
      </div>
    </FieldWrapper>
  );
};

// ─────────────────────────────────────────────────────────────
// FileUploadField
// ─────────────────────────────────────────────────────────────
export interface FileUploadFieldProps {
  id: string;
  label?: string;
  accept?: string;
  multiple?: boolean;
  error?: string;
  hint?: string;
  required?: boolean;
  onChange: (files: FileList | null) => void;
  value?: File[] | null;
}

export const FileUploadField: React.FC<FileUploadFieldProps> = ({
  id, label, accept, multiple = false, error, hint, required, onChange, value,
}) => {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    onChange(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  return (
    <FieldWrapper label={label} required={required} error={error} hint={hint} id={id}>
      <div
        className={`file-upload-zone ${dragOver ? 'file-upload-zone--over' : ''} ${error ? 'file-upload-zone--error' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={() => setDragOver(false)}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && inputRef.current?.click()}
        aria-label={label ? `Upload ${label}` : 'Upload file'}
      >
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={accept}
          multiple={multiple}
          className="file-upload-zone__input"
          onChange={e => onChange(e.target.files)}
          aria-hidden="true"
          tabIndex={-1}
        />
        <Upload size={24} className="file-upload-zone__icon" />
        <p className="file-upload-zone__text">
          Drag &amp; drop or <span className="file-upload-zone__browse">browse</span>
        </p>
        {accept && (
          <p className="file-upload-zone__hint">
            Accepted: {accept}
          </p>
        )}
      </div>
      {value && value.length > 0 && (
        <div className="file-upload-list">
          {Array.from(value).map((file, i) => (
            <div key={i} className="file-upload-list__item">
              <span className="file-upload-list__name">{file.name}</span>
              <span className="file-upload-list__size">
                ({(file.size / 1024).toFixed(1)} KB)
              </span>
              <button
                type="button"
                className="file-upload-list__remove"
                onClick={e => { e.stopPropagation(); }}
                aria-label={`Remove ${file.name}`}
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </FieldWrapper>
  );
};
